// src/lib/tryout/presetManifest.ts
/**
 * Lookup helpers for pre-baked preset assets. The manifest is a static JSON
 * shipped in public/tryout/presets/manifest.json; these helpers decide which
 * baked file to show and when a live AI call is unavoidable.
 */
import type { EmptyVariant } from './tryoutRun';

// Local copy of the 3 supported staging styles — avoids importing a UI module
// into lib. Must stay in sync with STAGING_STYLES in src/lib/roomConfig.ts.
export type StagingStyle = 'modern' | 'luxury' | 'industrial';

export interface PresetManifestEntry {
  room: string;
  empty: Record<'a' | 'b', string>;
  staged: Partial<Record<StagingStyle, Partial<Record<'a' | 'b', string>>>>;
}

export type PresetManifest = Record<string, PresetManifestEntry>;

export function bakedEmptyUrl(
  m: PresetManifest,
  presetId: string,
  variant: EmptyVariant,
): string | null {
  return m[presetId]?.empty[variant.toLowerCase() as 'a' | 'b'] ?? null;
}

export function bakedStagedUrl(
  m: PresetManifest,
  presetId: string,
  variant: EmptyVariant,
  style: StagingStyle,
): string | null {
  return m[presetId]?.staged[style]?.[variant.toLowerCase() as 'a' | 'b'] ?? null;
}

/**
 * A live staging call is needed when the source is an upload, when a preset
 * empty was refined (diverged from the bake), or when no baked staged asset
 * exists for the chosen style/variant.
 */
export function needsLiveStaging(p: {
  isPreset: boolean;
  wasRefined: boolean;
  bakedUrl: string | null;
}): boolean {
  if (!p.isPreset) return true;
  if (p.wasRefined) return true;
  return p.bakedUrl === null;
}
